/**
 * Smart Breadcrumbs Component
 * Generates breadcrumbs automatically from menu structure and route metadata
 */

import React from 'react';
import { Breadcrumb } from 'antd';
import { Link } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';
import { useMenuNavigation } from '@/hooks/useMenuNavigation';
import './SmartBreadcrumbs.css';

interface SmartBreadcrumbsProps {
  showHome?: boolean;
  maxItems?: number;
  separator?: React.ReactNode;
  style?: React.CSSProperties;
}

export const SmartBreadcrumbs: React.FC<SmartBreadcrumbsProps> = ({
  showHome = true,
  maxItems = 4,
  separator = '/',
  style,
}) => {
  const { breadcrumb, currentMenuItem, currentModule } = useMenuNavigation();

  // Build breadcrumb items
  const buildBreadcrumbItems = () => {
    const items: any[] = [];

    // Add home item
    if (showHome) {
      items.push({
        key: 'home',
        title: (
          <Link to="/dashboard">
            <HomeOutlined /> Home
          </Link>
        ),
      });
    }

    // Add breadcrumb items from menu structure
    if (breadcrumb && breadcrumb.length > 0) {
      breadcrumb.forEach((crumb, index) => {
        // Don't make the last item clickable
        if (index === breadcrumb.length - 1) {
          items.push({
            key: `crumb-${index}`,
            title: <span className="breadcrumb-current">{crumb}</span>,
          });
        } else if (index === 0 && currentModule) {
          // First item is the module - link to module dashboard if it exists
          const moduleDashboardRoute = `/modules/${currentModule.code.replace('_', '-')}`;
          items.push({
            key: `crumb-${index}`,
            title: <Link to={moduleDashboardRoute}>{crumb}</Link>,
          });
        } else {
          // Middle items - just display as text (could be enhanced to link if needed)
          items.push({
            key: `crumb-${index}`,
            title: <span>{crumb}</span>,
          });
        }
      });
    } else if (currentMenuItem) {
      // Fallback: If no breadcrumb but we have current menu item
      items.push({
        key: 'current',
        title: <span className="breadcrumb-current">{currentMenuItem.label}</span>,
      });
    }

    return items;
  };

  const items = buildBreadcrumbItems();

  // Don't render if no items (except home)
  if (items.length <= 1 && showHome) {
    return null;
  }

  return (
    <div className="smart-breadcrumbs-container" style={style}>
      <Breadcrumb
        items={items}
        separator={separator}
        className="smart-breadcrumbs"
      />
      {currentMenuItem && (
        <div className="breadcrumb-description">
          {currentMenuItem.description}
        </div>
      )}
    </div>
  );
};

export default SmartBreadcrumbs;
